import React, { useState } from 'react';
import { SearchIcon, FilterIcon, LayoutGridIcon, LayoutListIcon, PaletteIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
import { useDarkMode } from '../../context/DarkModeContext';
import { ProfileCard } from '../../components/Directory/ProfileCard';
import { mapCreativeToProfileCard } from '../../utils/profileCardMappers';
import { creativeProfessionals as creatives, CreativeCategory } from '../../data/creativeProfessionals';
const AllCreativeProfiles = () => {
  const {
    darkMode
  } = useDarkMode();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CreativeCategory>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState('name-asc');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Filter creatives based on search query and selected category
  const filteredCreatives = creatives.filter(creative => {
    const matchesSearch = searchQuery === '' || creative.name.toLowerCase().includes(searchQuery.toLowerCase()) || creative.title.toLowerCase().includes(searchQuery.toLowerCase()) || creative.specialties.some(specialty => specialty.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || creative.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });
  // Sort logic
  filteredCreatives.sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'rating-desc':
        return b.rating - a.rating;
      case 'reviews-desc':
        return b.reviewCount - a.reviewCount;
      default:
        return 0;
    }
  });
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'name-asc',
      label: 'A-Z'
    }, {
      value: 'name-desc',
      label: 'Z-A'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }, {
      value: 'reviews-desc',
      label: 'Most Reviews'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'category',
    title: 'Category',
    type: 'radio',
    options: [{
      value: 'all',
      label: 'All Creatives'
    }, {
      value: 'cover-design',
      label: 'Cover Design'
    }, {
      value: 'interior-art',
      label: 'Interior Art'
    }, {
      value: 'character-art',
      label: 'Character Art'
    }, {
      value: 'merch',
      label: 'Merch Design'
    }, {
      value: 'photography',
      label: 'Photography'
    }, {
      value: 'models',
      label: 'Models'
    }],
    value: selectedCategory,
    onChange: val => setSelectedCategory(val as CreativeCategory)
  }];
  return <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-white'}`} data-id="element-8525" data-mp="2zgxrnnlpeo0h">
      {/* Header Section */}
      <div className={`pt-24 md:pt-8 pb-6 px-4 sm:px-6 lg:px-8`} data-id="element-8526" data-mp="34e7dkt9vwhjk">
        <div className="max-w-7xl mx-auto" data-id="element-8527" data-mp="38g1cox4fldhm">
          <h1 className={`text-3xl font-bold mb-2 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`} data-id="element-8528" data-mp="38ezp5t68ayj0">
            Creative Arts Directory
          </h1>
          <p className={`text-lg max-w-3xl ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-8529" data-mp="3idp7fn21ekzs">
            Connect with talented creative professionals to bring your book to
            life. Find cover designers, interior artists, character
            illustrators, merchandise creators, photographers, and models.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-id="element-8530" data-mp="39j9jf5h56t68">
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-6" data-id="element-8531" data-mp="3nisrxzdv86j2">
          <button onClick={() => setSidebarOpen(true)} className={`w-full flex items-center justify-center px-4 py-3 border rounded-md ${darkMode ? 'bg-gray-800 border-gray-700 text-gray-200 hover:bg-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`} data-id="element-8532" data-mp="3nhq1ttoda847">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-8533" data-mp="2qq5ozhw3ocak" />
            <span data-id="element-8534" data-mp="34jww6jmczk34">Filters & Sort</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-8535" data-mp="2zsyd5c9zk7w6">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-8536" data-mp="2c3cin24k6gui">
            <FilterSidebar title="Filters" sections={filterSections} onReset={() => {
            setSelectedCategory('all');
            setSortBy('name-asc');
            setSearchQuery('');
          }} searchable={false} data-id="element-8537" data-mp="1xx24u889q9u8" />
          </FilterSidebarWrapper>

          {/* Main Content */}
          <main className="flex-1 min-w-0" data-id="element-8538" data-mp="2gnsag4p84aqs">
            {/* Search and View Toggle */}
            <div className={`rounded-xl shadow-sm p-4 mb-6 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} data-id="element-8539" data-mp="2ln4vql0g59r9">
              <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-8540" data-mp="26si63abcrr9u">
                <div className="relative flex-1" data-id="element-8541" data-mp="2bne02g05gopl">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-8542" data-mp="vah2qa7zopy3" />

                  <input type="text" placeholder="Search creatives by name, title, or specialty..." className={`pl-10 pr-4 py-2.5 w-full rounded-lg border focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors ${darkMode ? 'bg-gray-900 border-gray-700 text-gray-100 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-8543" data-mp="32wqvin3g7vud" />
                </div>
                <div className={`flex rounded-lg p-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-8544" data-mp="2f9itce1bh2wk">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? darkMode ? 'bg-gray-600 text-purple-400 shadow-sm' : 'bg-white text-purple-600 shadow-sm' : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} aria-label="Grid view" data-id="element-8545" data-mp="259i9mqzjckru">
                    <LayoutGridIcon className="h-5 w-5" data-id="element-8546" data-mp="fz642jn5rnme" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? darkMode ? 'bg-gray-600 text-purple-400 shadow-sm' : 'bg-white text-purple-600 shadow-sm' : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`} aria-label="List view" data-id="element-8547" data-mp="kqeglojsbp3s">
                    <LayoutListIcon className="h-5 w-5" data-id="element-8548" data-mp="1zeucv3octe7" />
                  </button>
                </div>
              </div>
            </div>

            {/* Results Header */}
            <div className="mb-6" data-id="element-8549" data-mp="1dg4ubwhi8ozd">
              <h2 className={`text-lg font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8550" data-mp="1detto5jcvxch">
                {filteredCreatives.length} creative professional
                {filteredCreatives.length !== 1 ? 's' : ''} found
              </h2>
            </div>

            {/* Results Section */}
            {filteredCreatives.length === 0 ? <div className={`text-center py-16 rounded-xl border-2 border-dashed ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-8551" data-mp="2d9lk0f241y3i">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-8552" data-mp="2m870m4o0v31n">
                  <PaletteIcon className={`h-8 w-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-8553" data-mp="2w7xuc44rde0o" />
                </div>
                <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-8554" data-mp="2r68m0w8x7ega">
                  No creative professionals found
                </h3>
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-8555" data-mp="38hniplwqh6e7">
                  Try adjusting your search or filter criteria to find more
                  results.
                </p>
              </div> : <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'} data-id="element-8556" data-mp="2yprkmaundg9z">
                {filteredCreatives.map(creative => <ProfileCard key={creative.id} data={mapCreativeToProfileCard(creative)} viewMode={viewMode} data-id="element-8557" data-mp="2urk33dsd0axq" />)}
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default AllCreativeProfiles;